<script setup lang="ts">
import { ref, onBeforeMount, computed, watch } from "vue";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { useApiClient } from "@/api/client";
import { useRoute } from "vue-router";
import dayjs from "dayjs";
import ConfigureConditionComparisonSelectionDatePicker from "@/modules/rule_engine/components/conditions/ConfigureConditionComparisonSelectionDatePicker.vue";
import VehicleDTO = App.Data.VehicleDTO;
import { v4 as uuidv4 } from "uuid";
import { ucfirst } from "chartjs-graph";
import { debounce } from "vue-debounce";
import { formatNumber } from "chartjs-graph";

const breadcrumbs = ref([
	{
		title: "Rule Based Engine",
		disabled: false,
		to: "/rule-engine",
	},
]);

const model_uuid: string | null =
	useRoute().params.model_uuid !== ""
		? String(useRoute().params.model_uuid)
		: null;

let selectedVersions: string | string[] | undefined = useRoute().query
	.versions as string[] | undefined;

const versionNames = ref([]);
const availableVehicles = ref<VehicleDTO[]>([]);
const availableCities = ref<{ vehicle_city: string }[]>([]);
const availableModels = ref<{ vehicle_brand_model: string }[]>([]);
const selectedCity = ref<{ vehicle_city: string } | null>(null);
const selectedModel = ref<{ vehicle_brand_model: string } | null>(null);
const isLoadingModule = ref<boolean>(true);
const isLoadingModelResponse = ref<boolean>(true);
const comparisonResponse = ref(null);
const selectedVehicles = ref<string[]>([]);
const isInputValid = ref<boolean>(false);

const bookingMoment = ref<string>("now");
const bookingMomentParsed = ref();
const periodFrom = ref<string>("now+1d/d");
const periodFromParsed = ref();
const periodTo = ref<string>("now+7d/d");
const periodToParsed = ref();

const personId = ref<string | null>(uuidv4());
const selectedModelVersion = ref(selectedVersions[0]);
const displayMetric = ref<"price_per_km" | "price_per_hour">("price_per_hour");
const displayMode = ref<
	| "computed_price"
	| "absolute_delta_from_base_price"
	| "relative_delta_from_base_price"
>("computed_price");

const displayModes = [
	{ mode: "computed_price", label: "Computed Price" },
	{
		mode: "absolute_delta_from_base_price",
		label: "Absolute Δ from base price",
	},
	{
		mode: "relative_delta_from_base_price",
		label: "Relative Δ from base price",
	},
];

const contractType = ref<"Other" | "B2C" | "B2B">("Other");

const contractTypes = [
	{ type: "Other", label: "Other" },
	{ type: "b2c", label: "B2C" },
	{ type: "b2b", label: "B2B" },
];

const allVehiclesSelected = computed(() => {
	return selectedVehicles.value.length === availableVehicles.value.length;
});
const someVehiclesSelected = computed(() => {
	return selectedVehicles.value.length > 0;
});

const toggleVehicleSelection = () => {
	if (allVehiclesSelected.value) {
		selectedVehicles.value = [];
	} else {
		selectedVehicles.value = availableVehicles.value.map(
			(vehicle) => vehicle.vehicle_uid
		);
	}
};

const completeBreadCrumbs = async () => {
	const response = await useApiClient("ruleEngine").showModelMeta(model_uuid);
	breadcrumbs.value.push({
		title: response.isOk() ? response.value.name : model_uuid,
		disabled: false,
		to: `/rule-engine/versions/${model_uuid}`,
	});
	breadcrumbs.value.push({
		title: "Comparison",
		disabled: true,
		to: `/rule-engine/versions/${model_uuid}/comparison`,
	});
};

const getMyWheelsAvailableCars = async () => {
	const params = {};
	if (selectedCity.value) {
		params["filter[vehicle_city]"] = selectedCity.value;
	}
	if (selectedModel.value) {
		params["filter[vehicle_brand_model]"] = selectedModel.value;
	}

	const response = await useApiClient("ruleEngine").getMyWheelsAvailableCars(
		params
	);
	if (response.isOk()) {
		availableVehicles.value = response.value.vehicles;
		availableCities.value = response.value.cities;
		availableModels.value = response.value.models;

		if (availableVehicles.value.length > 0) {
			selectedVehicles.value = [availableVehicles.value[0].vehicle_uid];
		} else {
			selectedVehicles.value = [];
			comparisonResponse.value = null;
		}
	}
};
watch(selectedCity, () => getMyWheelsAvailableCars());
watch(selectedModel, () => getMyWheelsAvailableCars());

const responseBody = computed(() => {
	return {
		vehicle_uuids: selectedVehicles.value,
		booking_moment: dayjs(bookingMomentParsed.value)
			.utc()
			.format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
		booking_start: dayjs(periodFromParsed.value)
			.utc()
			.format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
		booking_end: dayjs(periodToParsed.value)
			.utc()
			.format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
		properties: {
			person_id: personId.value,
			contract_category: "MyWheels Start",
			contract_type: contractType.value,
		},
		model_version_uuids: selectedVersions,
	};
});

const getMyWheelsData = debounce(async () => {
	if (isInputValid.value && selectedVehicles.value) {
		isLoadingModelResponse.value = true;

		const response =
			selectedVehicles.value.length > 1
				? await useApiClient("ruleEngine").introspectManyVehiclePricesAsAverage(
						responseBody.value
				  )
				: await useApiClient("ruleEngine").introspectVehiclePricing(
						selectedVehicles.value[0] ?? null,
						responseBody.value
				  );

		if (response.isOk()) {
			comparisonResponse.value = response.value?.results ?? null;
		} else {
			comparisonResponse.value = null;
		}

		isLoadingModelResponse.value = false;
	}
}, 250);

watch(
	responseBody,
	() => {
		if (!isLoadingModule.value) {
			getMyWheelsData();
		}
	},
	{
		deep: true,
	}
);

function transformIntrospectData(dataInput: any) {
	const entries = dataInput?.introspect || {};
	const localZone = "Europe/Amsterdam";

	const grouped: Record<
		string,
		Record<string, { metric?: string; baseMetric?: string }>
	> = {};

	for (const [timestamp, value] of Object.entries(entries)) {
		const localTime = dayjs.utc(timestamp).tz(localZone);
		const date = localTime.format("YYYY-MM-DD");
		const hour = localTime.format("HH");

		if (!grouped[date]) grouped[date] = {};

		grouped[date][hour] = {
			metric: (value as any)[displayMetric.value],
			baseMetric: (value as any)[`base_${displayMetric.value}`],
		};
	}

	const sortedDates = Object.keys(grouped).sort();
	const hourlyMatrix: Record<string, string>[] = [];

	for (let hour = 0; hour < 24; hour++) {
		const hourStr = hour.toString().padStart(2, "0");
		const row: Record<number, string> = {
			hour: `${hourStr}:00`,
		};

		for (const date of sortedDates) {
			const entry = grouped[date]?.[hourStr];

			if (entry) {
				const metric = entry.metric;
				const baseMetric = entry.baseMetric;

				row[date] = metric !== undefined ? parseFloat(metric).toFixed(2) : 0;
				row[`base_${date}`] =
					baseMetric !== undefined ? parseFloat(baseMetric).toFixed(2) : 0;
			} else {
				row[date] = 0;
				row[`base_${date}`] = 0;
			}
		}

		hourlyMatrix.push(row);
	}

	return hourlyMatrix;
}

const transformedIntrospectData = computed(() => {
	if (!comparisonResponse.value) {
		return [];
	}
	return (
		transformIntrospectData(
			comparisonResponse.value[selectedModelVersion.value]
		) ?? []
	);
});

const availableDateColumns = computed(() => {
	if (!transformedIntrospectData.value.length) return [];

	const firstRow = transformedIntrospectData.value[0];

	return Object.keys(firstRow)
		.filter((key) => !key.startsWith("base_"))
		.map((date) => ({
			title: ucfirst(date),
			key: date,
			sortable: false,
			align: date === "hour" ? "start" : "end",
			nowrap: true,
		}));
});

const getVersionName = async (versionUuid: string) => {
	const response = await useApiClient("ruleEngine").showVersion(
		model_uuid,
		versionUuid
	);
	if (response.isOk()) {
		versionNames.value[versionUuid] = response.value.name ?? "Unknown";
	}
};

const randomizePersonId = () => {
	personId.value = uuidv4();
};

onBeforeMount(async () => {
	if (typeof selectedVersions === "string") {
		selectedVersions = [selectedVersions];
		selectedModelVersion.value = selectedVersions;
	}
	selectedVersions.forEach((versionUuid) => getVersionName(versionUuid));
	isLoadingModule.value = true;
	await Promise.all([getMyWheelsAvailableCars(), completeBreadCrumbs()]);
	isLoadingModule.value = false;
});
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />
	<v-card>
		<v-progress-linear indeterminate v-if="isLoadingModule"></v-progress-linear>
		<div v-else class="pa-5">
			<!--		In the future we probably want to refactor the input's to be rendered based on the rule-dimensions		-->
			<v-row>
				<v-col>
					<div class="d-flex">
						<div class="pr-5 pt-3 label-column">City:</div>
						<div class="w-100">
							<v-select
								variant="filled"
								v-model="selectedCity"
								:items="availableCities"
								item-title="vehicle_city"
								item-value="vehicle_city"
								clearable
							/>
						</div>
					</div>
				</v-col>
				<v-col>
					<div class="d-flex">
						<div class="pr-5 pt-3 label-column">Model:</div>
						<div class="w-100">
							<v-select
								variant="filled"
								v-model="selectedModel"
								:items="availableModels"
								item-title="vehicle_brand_model"
								item-value="vehicle_brand_model"
								clearable
							/>
						</div>
					</div>
				</v-col>
			</v-row>
			<div class="d-flex">
				<div class="pr-5 pt-3 label-column">Vehicle:</div>
				<div class="w-100">
					<v-select
						v-model="selectedVehicles"
						:items="availableVehicles"
						item-title="vehicle_alias"
						item-value="vehicle_uid"
						multiple
					>
						<template v-slot:selection="{ item, index }">
							<v-chip v-if="index < 2" :text="item.title"></v-chip>
							<span
								v-if="index === 2"
								class="text-grey text-caption align-self-center"
							>
								(+{{ selectedVehicles.length - 2 }} others)
							</span>
						</template>

						<template v-if="availableVehicles.length < 200" v-slot:prepend-item>
							<v-list-item title="Select All" @click="toggleVehicleSelection">
								<template v-slot:prepend>
									<v-checkbox-btn
										:color="
											someVehiclesSelected ? 'indigo-darken-4' : undefined
										"
										:indeterminate="
											someVehiclesSelected && !allVehiclesSelected
										"
										:model-value="allVehiclesSelected"
									></v-checkbox-btn>
								</template>
							</v-list-item>

							<v-divider class="mt-2"></v-divider>
						</template>
					</v-select>
				</div>
			</div>
			<div class="d-flex">
				<div class="pr-5 pt-3 label-column">Booking moment:</div>
				<div class="input-container-date">
					<configure-condition-comparison-selection-date-picker
						v-model="bookingMoment"
						v-model:parsed="bookingMomentParsed"
						v-model:is-input-valid="isInputValid"
						:show-help="true"
					/>
				</div>
			</div>
			<div class="d-flex">
				<div class="pr-5 pt-3 label-column">Booking (from/to):</div>
				<div class="input-container-date">
					<configure-condition-comparison-selection-date-picker
						v-model="periodFrom"
						v-model:parsed="periodFromParsed"
						v-model:is-input-valid="isInputValid"
						:show-help="false"
					/>
				</div>
				<div class="input-container-date ml-5">
					<configure-condition-comparison-selection-date-picker
						v-model="periodTo"
						:round-up="true"
						v-model:parsed="periodToParsed"
						v-model:is-input-valid="isInputValid"
					/>
				</div>
			</div>
			<div class="d-flex">
				<div class="pr-5 pt-3 label-column">Person ID:</div>
				<div class="input-container-date d-flex align-center">
					<v-text-field
						hide-details="auto"
						variant="filled"
						v-model="personId"
						placeholder="CustomerID"
					/>
					<v-btn
						class="ml-2"
						variant="flat"
						color="secondary"
						height="48"
						@click="randomizePersonId"
					>
						Random
					</v-btn>
				</div>
			</div>
			<div class="d-flex pt-5">
				<div class="pr-5 pt-3 label-column">Contract Type:</div>
				<div class="input-container-date">
					<v-select
						variant="filled"
						v-model="contractType"
						:items="contractTypes"
						item-title="label"
						item-value="type"
						clearable
					/>
				</div>
			</div>
			<v-divider class="my-5" />
			<div>
				<v-progress-linear
					indeterminate
					v-if="isLoadingModelResponse"
				></v-progress-linear>
				<div v-else-if="comparisonResponse">
					<v-item-group
						selected-class="selected-class"
						v-model="selectedModelVersion"
						mandatory
					>
						<div class="d-flex" style="gap: 16px">
							<!--			map to response			-->
							<div
								v-for="(version, uuid) in comparisonResponse"
								:key="uuid"
								class="w-100"
							>
								<v-item
									:value="uuid"
									v-slot="{ isSelected, selectedClass, toggle }"
								>
									<v-card
										class="v-sheet--offset mx-auto d-flex"
										:class="[selectedClass]"
										@click="toggle"
									>
										<div class="d-flex pa-5 w-100">
											<div class="d-flex h-100 align-center prepend">
												<v-checkbox
													disabled
													hide-details
													color="primary"
													:value="uuid"
													v-model="selectedModelVersion"
												></v-checkbox>
											</div>

											<v-divider class="mx-5" vertical />

											<div class="w-100">
												<div class="h-100 w-100">
													<div class="d-flex">
														<v-card-title
															v-tooltip="`${versionNames[uuid] ?? '-'}`"
															:style="`max-width: ${
																selectedVersions.length === 1
																	? 900
																	: selectedVersions.length === 2
																	? 300
																	: 150
															}px`"
															class="pl-0"
															>{{ versionNames[uuid] ?? "-" }}</v-card-title
														>
														<v-spacer />
														<v-chip
															variant="flat"
															density="compact"
															class="mt-2"
														>
															<span class="text-caption text-gray-100"
																>...{{ uuid.toString().slice(-5) }}</span
															>
														</v-chip>
													</div>
													<v-divider class="my-2" />
													<div class="d-flex">
														<div>
															<div class="d-flex py-1">
																<div>Hourly Rate:</div>
																<div class="pl-10">
																	<v-chip
																		density="compact"
																		color="primary"
																		label
																	>
																		{{
																			formatNumber(
																				version.price.per_hour,
																				"money"
																			)
																		}}
																		<span class="text-caption ml-2"
																			>/hr</span
																		></v-chip
																	>
																</div>
															</div>
															<div class="d-flex py-1">
																<div>Kilometer Rate:</div>
																<div class="pl-10">
																	<v-chip
																		density="compact"
																		color="primary"
																		label
																	>
																		{{
																			formatNumber(
																				version.price.per_km,
																				"money"
																			)
																		}}
																		<span class="text-caption ml-2">/km</span>
																	</v-chip>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</v-card>
								</v-item>
							</div>
						</div>
					</v-item-group>
				</div>
				<div v-else>Unable to load a response for this configuration.</div>
			</div>
		</div>
		<v-fade-transition class="pa-5">
			<div v-if="!isLoadingModule && !isLoadingModelResponse">
				<div class="d-flex pt-5">
					<v-spacer />
					<div class="mt-1">
						<v-radio-group v-model="displayMetric" inline hide-details>
							<v-radio
								color="primary"
								value="price_per_hour"
								label="Price per hour"
							/>
							<v-radio
								color="primary"
								value="price_per_km"
								label="Price per kilometer"
							/>
						</v-radio-group>
					</div>
					<v-divider vertical class="mx-5" />
					<div>
						<v-select
							hide-details
							v-model="displayMode"
							:items="displayModes"
							item-value="mode"
							item-title="label"
						/>
					</div>
				</div>

				<v-data-table
					class="pt-5"
					density="compact"
					:headers="availableDateColumns"
					:headerProps="{ class: 'custom-header' }"
					:items="transformedIntrospectData"
					:items-per-page="24"
				>
					<template #item="{ item, columns }">
						<tr>
							<td
								v-for="column in columns"
								:key="column.key"
								:class="column.key !== 'hour' ? 'delta-cell' : ''"
								:style="
									(parseFloat(item[column.key]) -
										parseFloat(item[`base_${column.key}`])) /
										parseFloat(item[`base_${column.key}`]) ===
									0
										? {}
										: {
												'--delta':
													((parseFloat(item[column.key]) -
														parseFloat(item[`base_${column.key}`])) /
														parseFloat(item[`base_${column.key}`])) *
													100,
										  }
								"
							>
								<div
									v-if="column.key === 'hour'"
									:class="column.align === 'end' ? 'text-right' : 'text-left'"
								>
									<strong>{{ item.hour }}</strong>
								</div>
								<div v-else class="d-flex">
									<div
										class="w-100"
										:class="column.align === 'end' ? 'text-right' : 'text-left'"
									>
										<span v-if="parseFloat(item[column.key]) !== 0">
											<template
												v-if="displayMode === 'absolute_delta_from_base_price'"
											>
												{{
													formatNumber(
														parseFloat(item[column.key]) -
															parseFloat(item[`base_${column.key}`]),
														"money"
													)
												}}
											</template>
											<template
												v-if="displayMode === 'relative_delta_from_base_price'"
											>
												{{
													formatNumber(
														parseFloat(item[`base_${column.key}`]) === 0
															? 0
															: (parseFloat(item[column.key]) -
																	parseFloat(item[`base_${column.key}`])) /
																	parseFloat(item[`base_${column.key}`]),
														"percentage"
													)
												}}
											</template>
											<template v-if="displayMode === 'computed_price'">
												{{
													formatNumber(parseFloat(item[column.key]), "money")
												}}
											</template>
										</span>
										<span v-else>-</span>
									</div>
									<div v-if="parseFloat(item[column.key]) !== 0">
										<v-icon
											v-tooltip="
												`${(parseFloat(item[`base_${column.key}`]) === 0
													? 0
													: ((parseFloat(item[column.key]) -
															parseFloat(item[`base_${column.key}`])) /
															parseFloat(item[`base_${column.key}`])) *
													  100
												).toFixed(1)}% up from base price (${formatNumber(
													parseFloat(item[`base_${column.key}`]),
													'money'
												)})`
											"
											icon="mdi-chevron-up"
											:key="`${selectedModelVersion}-${column.key}-icon-up`"
											v-if="item[column.key] > item[`base_${column.key}`]"
										/>
										<v-icon
											v-tooltip="
												`${(parseFloat(item[`base_${column.key}`]) === 0
													? 0
													: ((parseFloat(item[column.key]) -
															parseFloat(item[`base_${column.key}`])) /
															parseFloat(item[`base_${column.key}`])) *
													  100
												).toFixed(1)}% down from base price (${formatNumber(
													parseFloat(item[`base_${column.key}`]),
													'money'
												)})`
											"
											icon="mdi-chevron-down"
											:key="`${selectedModelVersion}-${column.key}-icon-down`"
											v-else-if="item[column.key] < item[`base_${column.key}`]"
										/>
										<v-icon
											v-tooltip="`Same value as the base-price`"
											icon="mdi-minus"
											:key="`${selectedModelVersion}-${column.key}-icon-minus`"
											v-else
										/>
									</div>
								</div>
							</td>
						</tr>
					</template>
					<template #bottom> </template>
				</v-data-table>
			</div>
		</v-fade-transition>
	</v-card>
</template>

<style>
.label-column {
	width: 250px;
	min-width: 250px;
}
.input-container-date {
	width: 300px;
}
.custom-header {
	background-color: rgb(var(--color-background));
}
.selected-class {
	border: 2px solid #3aa396;
}
.align-center {
	align-items: center;
}
.prepend {
	width: 40px !important;
}
.delta-cell {
	background-color: #fff;
}

.delta-cell {
	background-color: #fff;
}

.delta-cell[style*="--delta"] {
	--d: clamp(-100, var(--delta), 100);

	--mag: max(var(--d), calc(var(--d) * -1));
	--t: calc(var(--mag) / 100);

	--pos: clamp(0, var(--d), 1);

	--r-red: 239;
	--g-red: 83;
	--b-red: 80;

	--r-grn: 102;
	--g-grn: 187;
	--b-grn: 106;

	--r: calc(var(--r-red) * (1 - var(--pos)) + var(--r-grn) * var(--pos));
	--g: calc(var(--g-red) * (1 - var(--pos)) + var(--g-grn) * var(--pos));
	--b: calc(var(--b-red) * (1 - var(--pos)) + var(--b-grn) * var(--pos));

	--min-a: 0.12; /* lightest tint */
	--max-a: 0.7; /* darkest tint */

	--alpha: calc(var(--min-a) + (var(--max-a) - var(--min-a)) * var(--t));

	background-color: rgba(var(--r), var(--g), var(--b), var(--alpha));
}
</style>
